import React, { useState } from 'react';
import { useT } from '../i18n/LanguageContext';
import { api, ApiError } from '../api/client';

const INITIAL = {
  nome:      '',
  email:     '',
  assunto:   '',
  mensagem:  '',
};

/**
 * ContactForm — Formulário de contacto
 *
 * Props:
 *   dark {boolean} — Versão com inputs para fundo escuro
 *   onSubmit {function} — Callback com os dados do formulário
 */
export default function ContactForm({ dark = false, onSubmit }) {
  const t = useT();
  const [fields, setFields]   = useState(INITIAL);
  const [errors, setErrors]   = useState({});
  const [sent,   setSent]     = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const inputClass = `form-input${dark ? ' form-input--dark' : ''}`;
  const textareaClass = `form-textarea${dark ? ' form-textarea--dark' : ''}`;

  /* ── Validação ── */
  function validate() {
    const e = {};
    if (!fields.nome.trim())      e.nome     = t('form.errorName');
    if (!fields.email.trim())     e.email    = t('form.errorEmailReq');
    else if (!/\S+@\S+\.\S+/.test(fields.email))
                                   e.email   = t('form.errorEmailInvalid');
    if (!fields.mensagem.trim())  e.mensagem = t('form.errorMsg');
    return e;
  }

  /* ── Submissão ── */
  async function handleSubmit(e) {
    e.preventDefault();
    const e2 = validate();
    if (Object.keys(e2).length) { setErrors(e2); return; }

    setLoading(true);
    setSubmitError('');
    try {
      await api.sendContact(fields);
      if (onSubmit) onSubmit(fields);
      setSent(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        setErrors(err.fields);
      } else if (err instanceof ApiError && err.status === 429) {
        setSubmitError(t('form.errorRateLimited', { seconds: err.retryAfter || 60 }));
      } else {
        setSubmitError(t('form.errorGeneric'));
      }
    } finally {
      setLoading(false);
    }
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setFields(f => ({ ...f, [name]: value }));
    if (errors[name]) setErrors(e2 => ({ ...e2, [name]: '' }));
  }

  /* ── Enviado ── */
  if (sent) {
    const sentBodyLines = t('form.sentBody').split('\n');
    return (
      <div style={{ textAlign: 'center', padding: 'var(--sp-12) 0' }}>
        <div style={{
          width: 56, height: 56, borderRadius: '50%',
          border: '1px solid #000000',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto var(--sp-6)',
        }}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path d="M4 10l4.5 4.5L16 6" stroke="#000000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <p className="t-label" style={{ color: dark ? '#ffffff' : '#000000', marginBottom: 'var(--sp-3)' }}>
          {t('form.sentLabel')}
        </p>
        <p className={`t-body-lg${dark ? ' t-white' : ''}`} style={{ opacity: 0.75 }}>
          {sentBodyLines.map((line, i) => (
            <React.Fragment key={i}>
              {line}{i < sentBodyLines.length - 1 && <br />}
            </React.Fragment>
          ))}
        </p>
        <button
          className={`btn btn-sm${dark ? ' btn--on-dark' : ''}`}
          onClick={() => { setSent(false); setFields(INITIAL); }}
          style={{ marginTop: 'var(--sp-8)' }}
        >
          {t('form.sentNew')}
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="form-grid" style={{ marginBottom: 'var(--sp-6)' }}>

        {/* Nome */}
        <div className="form-group">
          <label htmlFor="cf-nome" className="form-label"
                 style={{ color: dark ? 'rgba(255,255,255,0.5)' : undefined }}>
            {t('form.name')}
          </label>
          <input
            id="cf-nome"
            name="nome"
            type="text"
            autoComplete="name"
            className={inputClass}
            placeholder={t('form.namePlaceholder')}
            value={fields.nome}
            onChange={handleChange}
            aria-describedby={errors.nome ? 'err-nome' : undefined}
          />
          {errors.nome && <p id="err-nome" className="form-error">{errors.nome}</p>}
        </div>

        {/* Email */}
        <div className="form-group">
          <label htmlFor="cf-email" className="form-label"
                 style={{ color: dark ? 'rgba(255,255,255,0.5)' : undefined }}>
            {t('form.email')}
          </label>
          <input
            id="cf-email"
            name="email"
            type="email"
            autoComplete="email"
            className={inputClass}
            placeholder={t('form.emailPlaceholder')}
            value={fields.email}
            onChange={handleChange}
            aria-describedby={errors.email ? 'err-email' : undefined}
          />
          {errors.email && <p id="err-email" className="form-error">{errors.email}</p>}
        </div>
      </div>

      {/* Assunto */}
      <div className="form-group" style={{ marginBottom: 'var(--sp-6)' }}>
        <label htmlFor="cf-assunto" className="form-label"
               style={{ color: dark ? 'rgba(255,255,255,0.5)' : undefined }}>
          {t('form.subject')}
        </label>
        <input
          id="cf-assunto"
          name="assunto"
          type="text"
          className={inputClass}
          placeholder={t('form.subjectPlaceholder')}
          value={fields.assunto}
          onChange={handleChange}
        />
      </div>

      {/* Mensagem */}
      <div className="form-group" style={{ marginBottom: 'var(--sp-10)' }}>
        <label htmlFor="cf-mensagem" className="form-label"
               style={{ color: dark ? 'rgba(255,255,255,0.5)' : undefined }}>
          {t('form.message')}
        </label>
        <textarea
          id="cf-mensagem"
          name="mensagem"
          className={textareaClass}
          placeholder={t('form.messagePlaceholder')}
          value={fields.mensagem}
          onChange={handleChange}
          rows={5}
          aria-describedby={errors.mensagem ? 'err-mensagem' : undefined}
        />
        {errors.mensagem && <p id="err-mensagem" className="form-error">{errors.mensagem}</p>}
      </div>

      {submitError && <p className="form-error" style={{ marginBottom: 'var(--sp-6)' }}>{submitError}</p>}

      <button
        type="submit"
        className={`btn btn-lg${dark ? ' btn--on-dark' : ''}`}
        disabled={loading}
      >
        {loading ? t('form.sending') : t('form.submit')}
      </button>
    </form>
  );
}
